/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionExpandAll';

const ActionExpandAllManager: ActionManagerFn<'ActionExpandAll'> = (
  platform: Platform,
): ActionManager<'ActionExpandAll'> => {
  return {
    ...baseActionManager(platform, 'ActionExpandAll'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Target Grid Item ID',
        type: 'ItemID',
        required: true,
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'TreeColumn') {
            return `Item id '${value}' doesn't belong to a TreeColumn`;
          }
          return '';
        },
      },
    },
    name: 'Expand All',
    icon: { iconName: 'up-right-and-down-left-from-center' },
    description: `**Overview**<br/>
The Expand All action enables users to programmatically expand every collapsible parent row within a specific TreeColumn component. This utility is designed for hierarchical data structures, allowing users to instantly transition from a collapsed view to a fully revealed tree structure with a single interaction.

**Key Features**<br/>
* **Global Expansion:** Triggers the opening of all nested nodes and child items within the target tree hierarchy.
* **Strict Component Validation:** Includes built-in safeguards to ensure the action is only linked to valid TreeColumn components, preventing configuration errors.
* **Deep Hierarchy Support:** Effective for multi-level nested data structures, revealing all layers of depth simultaneously.

**Properties**<br/>
* **Target Grid Item ID (Required):** The unique identifier of the specific component to control.
* *Constraint:* The target must be a valid TreeColumn type. If a non-tree component is selected, the system will prevent configuration and display a validation error.

**Use Cases**<br/>
* **Data Exploration:** Quickly reveal all hierarchical relationships in complex datasets.
* **Navigation:** Provide users with instant visibility of all nested content (such as file directories or organizational charts) without manual clicking.
* **Data Audit:** Enable comprehensive data inspection by ensuring no rows remain hidden.`,
  };
};

export default ActionExpandAllManager;
